"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "./scroll-area";

/**
 * TimePicker Component
 * 
 * Uses minutes since midnight (0-1439) as the value format:
 * - 12:00 AM (midnight) = 0 minutes
 * - 3:30 AM = 210 minutes (3*60 + 30)
 * - 12:00 PM (noon) = 720 minutes
 * - 11:59 PM = 1439 minutes
 * 
 * Minutes are displayed in 5-minute intervals for better UX.
 */


interface TimeValue {
  hour: number;
  minute: number;
  period?: "AM" | "PM";
}

function minutesToTimeValue(totalMinutes: number, format: "12h" | "24h"): TimeValue {
  // Ensure minutes are within valid range (0-1439)
  const clampedMinutes = Math.max(0, Math.min(1439, totalMinutes || 0));

  const hours24 = Math.floor(clampedMinutes / 60);
  const minutes = clampedMinutes % 60;

  if (format === "12h") {
    let hour12 = hours24 % 12;
    if (hour12 === 0) hour12 = 12; // Convert 0 to 12 for 12-hour format
    const period: "AM" | "PM" = hours24 >= 12 ? "PM" : "AM";

    return { hour: hour12, minute: minutes, period };
  } else {
    return { hour: hours24, minute: minutes };
  }
}

function timeValueToMinutes(time: TimeValue, format: "12h" | "24h"): number {
  let hours24 = time.hour;

  if (format === "12h") {
    if (time.period === "AM") {
      if (time.hour === 12) hours24 = 0; // 12 AM = 0 hours
    } else if (time.period === "PM") {
      if (time.hour !== 12) hours24 = time.hour + 12; // 1-11 PM = 13-23 hours
      // 12 PM = 12 hours (no change)
    }
  }

  return hours24 * 60 + time.minute;
}

export interface TimePickerProps {
  value?: number; // Minutes since midnight (0-1439)
  onChange?: (minutes: number) => void;
  format?: "12h" | "24h";
  className?: string;
  disabled?: boolean;
  variant?: "default" | "theme"; // Controls styling variant like Select component
}

function TimePicker({ value, onChange, format = "12h", className, disabled = false, variant = "theme" }: TimePickerProps) {
  const timeValue = minutesToTimeValue(value || 0, format);

  const hours = React.useMemo(() => {
    if (format === "12h") {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    } else {
      return Array.from({ length: 24 }, (_, i) => i);
    }
  }, [format]);

  const minutes = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i * 5);
  }, []);

  const periods = ["AM", "PM"];

  const handleTimeChange = (newHour?: number, newMinute?: number, newPeriod?: "AM" | "PM") => {
    if (disabled) return;

    const updatedTime: TimeValue = {
      hour: newHour !== undefined ? newHour : timeValue.hour,
      minute: newMinute !== undefined ? newMinute : timeValue.minute,
      period: format === "12h" ? (newPeriod !== undefined ? newPeriod : timeValue.period) : undefined,
    };

    const totalMinutes = timeValueToMinutes(updatedTime, format);
    onChange?.(totalMinutes);
  };

  const scrollToValue = (containerRef: React.RefObject<HTMLDivElement | null>, value: number, itemHeight: number = 32) => {
    if (containerRef.current) {
      const scrollTop = value * itemHeight - containerRef.current.clientHeight / 2 + itemHeight / 2;
      containerRef.current.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });
    }
  };

  const hourScrollRef = React.useRef<HTMLDivElement>(null);
  const minuteScrollRef = React.useRef<HTMLDivElement>(null);
  const periodScrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Scroll to current values when component mounts or value changes
    const hourIndex = format === "12h" ? timeValue.hour - 1 : timeValue.hour;
    scrollToValue(hourScrollRef, hourIndex);

    // Find the closest 5-minute interval for scrolling
    const minuteIndex = Math.round(timeValue.minute / 5);
    scrollToValue(minuteScrollRef, minuteIndex);

    if (format === "12h" && timeValue.period) {
      const periodIndex = periods.indexOf(timeValue.period);
      scrollToValue(periodScrollRef, periodIndex);
    }
  }, [value, format]);

  return (
    <div
      className={cn(
        "flex items-center",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {/* Hours Column */}
      <div className="flex flex-col items-center">
        <ScrollArea className={cn("w-16 border-r", variant === "theme" ? "border-theme" : "border-border")}>
          <div ref={hourScrollRef} className="p-1">
            {hours.map((hour) => (
              <button
                key={hour}
                disabled={disabled}
                onClick={() => handleTimeChange(hour)}
                className={cn(
                  "cursor-pointer w-full h-8 flex items-center justify-center text-sm rounded-md transition-colors",
                  timeValue.hour === hour 
                    ? variant === "theme" ? "bg-theme-text-0 text-theme-bg-0" : "bg-primary text-primary-foreground"
                    : variant === "theme" ? "hover:bg-theme-2" : "hover:bg-accent hover:text-accent-foreground",
                  disabled && "cursor-not-allowed"
                )}
              >
                {hour.toString().padStart(2, "0")}
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Minutes Column */}
        <ScrollArea className="w-16 h-full">
          <div ref={minuteScrollRef} className="p-1">
            {minutes.map((minute) => (
              <button
                key={minute}
                disabled={disabled}
                onClick={() => handleTimeChange(undefined, minute)}
                className={cn(
                  "cursor-pointer w-full h-8 flex items-center justify-center text-sm rounded-md transition-colors",
                  Math.round(timeValue.minute / 5) * 5 === minute 
                    ? variant === "theme" ? "bg-theme-text-0 text-theme-bg-0" : "bg-primary text-primary-foreground"
                    : variant === "theme" ? "hover:bg-theme-2" : "hover:bg-accent hover:text-accent-foreground",
                  disabled && "cursor-not-allowed"
                )}
              >
                {minute.toString().padStart(2, "0")}
              </button>
            ))}
          </div>
        </ScrollArea>

      {/* AM/PM Column for 12h format */}
      {format === "12h" && (
        <ScrollArea className={cn("w-16 border-l h-full", variant === "theme" ? "border-theme" : "border-border")}>
          <div ref={periodScrollRef} className="p-1">
            {periods.map((period) => (
              <button
                key={period}
                disabled={disabled}
                onClick={() => handleTimeChange(undefined, undefined, period as "AM" | "PM")}
                className={cn(
                  "cursor-pointer w-full h-8 flex items-center justify-center text-sm rounded-md transition-colors",
                  timeValue.period === period 
                    ? variant === "theme" ? "bg-theme-text-0 text-theme-bg-0" : "bg-primary text-primary-foreground"
                    : variant === "theme" ? "hover:bg-theme-2" : "hover:bg-accent hover:text-accent-foreground",
                  disabled && "cursor-not-allowed"
                )}
              >
                {period}
              </button>
            ))}
          </div>
        </ScrollArea>
      )
      }
    </div >
  );
}

// Helper function to convert minutes since midnight to a formatted time string
function minutesToTimeString(totalMinutes: number, format: "12h" | "24h" = "12h"): string {
  const timeValue = minutesToTimeValue(totalMinutes, format);

  if (format === "12h") {
    const hourStr = timeValue.hour.toString().padStart(2, "0");
    const minuteStr = timeValue.minute.toString().padStart(2, "0");
    return `${hourStr}:${minuteStr} ${timeValue.period}`;
  } else {
    const hourStr = timeValue.hour.toString().padStart(2, "0");
    const minuteStr = timeValue.minute.toString().padStart(2, "0");
    return `${hourStr}:${minuteStr}`;
  }
}

export { TimePicker, minutesToTimeString, type TimeValue };
